import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { CaptchaType } from "@gloria-public/register/domain/interfaces/captcha.interface";
import { CaptchaService } from "@gloria-public/register/domain/services/captcha.service";
import { TranslateService } from "@ngx-translate/core";
import { SessionService } from "@shared/services/session.service";
import { Subject, first } from "rxjs";

@Component({
  selector: "app-captcha-dialog",
  templateUrl: "./captcha-dialog.component.html",
  styleUrls: ["./captcha-dialog.component.scss"],
})
export class CaptchaDialogComponent implements OnInit, OnDestroy {
  public captchaImage: unknown;
  private captchaId = "";
  public userAnswer = "";
  public isLoading = true;
  public progressValue = 100;
  private timerValue = 30;
  private repeatCount = 0;
  private maxRepeats = 5;
  private interval!: any;

  /** Emits when the component is destroyed, used to complete all active subscriptions */
  private onDestroy$ = new Subject<void>();

  constructor(
    private captchaService: CaptchaService,
    private dialogRef: MatDialogRef<CaptchaDialogComponent>,
    public snackBar: MatSnackBar,
    private translateService: TranslateService,
    private sessionService: SessionService
  ) {}

  ngOnInit(): void {
    this.getCaptcha();
    this.startTimer();
  }

  /**
   * Cleans up subscriptions, subjects and interval when the component is destroyed.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  /**
   * Starts or restarts captcha countdown timer
   */
  public startTimer(): void {
    this.interval = setInterval(() => {
      if (this.timerValue > 0) {
        this.timerValue -= 1; // Decrease the timer value
        this.progressValue -= 3.4; // Decrease the progress value
      } else {
        clearInterval(this.interval); // Stop the interval once it finishes
        this.onNewCaptcha(); // Refresh captcha

        if (this.repeatCount < this.maxRepeats) {
          this.startTimer(); // Restart the timer if we haven't reached maxRepeats
        } else {
          this.snackBar.open(`❗ ${this.translateService.instant("page.registration.captcha.error.maxTry")}`, "OK", {
            duration: 5000,
          });
          this.dialogRef.close(false);
        }
      }
    }, 1000);
  }

  /**
   * Requests a new captcha image
   */
  public getCaptcha(): void {
    this.captchaService
      .generateCaptcha(CaptchaType.REGISTRATION)
      .pipe(first())
      .subscribe((res) => {
        const reader = new FileReader();
        reader.addEventListener(
          "load",
          () => {
            this.captchaImage = reader.result;
          },
          false
        );

        if (res.body) {
          reader.readAsDataURL(res.body);
          this.captchaId = res.headers.get("x-captcha-id") as string;
          this.isLoading = false;
          this.progressValue = 100;
          this.timerValue = 30;
        }
      });
  }

  /**
   * Verifies captcha answer
   */
  public onVerifyCaptcha(): void {
    this.captchaService
      .verifyCaptcha(this.captchaId, this.userAnswer)
      .pipe(first())
      .subscribe({
        next: (res) => {
          this.sessionService.accountCreationToken = res;
          this.snackBar.open(`✅ ${this.translateService.instant("page.registration.captcha.success")}`, "OK", {
            duration: 5000,
          });
          this.dialogRef.close(true);
        },
        error: (err) => {
          if (err.status === 400) {
            this.snackBar.open(`❗ ${this.translateService.instant("page.registration.captcha.error.invalidId")}`, "OK", {
              duration: 5000,
            });
          } else if (err.status === 403) {
            this.snackBar.open(`❗ ${this.translateService.instant("page.registration.captcha.error.wrongAnswer")}`, "OK", {
              duration: 5000,
            });
          } else if (err.status === 404) {
            this.snackBar.open(`❗ ${this.translateService.instant("page.registration.captcha.error.notFound")}`, "OK", {
              duration: 5000,
            });
          } else if (err.status === 410) {
            this.snackBar.open(`❗ ${this.translateService.instant("page.registration.captcha.error.expired")}`, "OK", {
              duration: 5000,
            });
          } else {
            this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
              duration: 5000,
            });
          }
          this.onNewCaptcha();
        },
      });
  }

  /**
   * Loads a new captcha and resets input state
   */
  public onNewCaptcha(): void {
    this.isLoading = true;
    this.userAnswer = "";
    this.repeatCount += 1;
    this.getCaptcha();
  }

  /**
   * Closes captcha dialog without validation
   */
  public close(): void {
    this.dialogRef.close(false);
  }
}
